import Swal from "sweetalert2";
import { useEffect, useState } from "react";
import Skeleton from "react-loading-skeleton";

// Services
import { api } from "@service/api";

// Components
import { Card } from "@components/cards/card";

// Hooks
import {
  getPlatformLogContentLoadedInfo,
  getPlatformLogPingInfo,
  getPlatformLogTypeInfo,
} from "@hooks/functions/PlatformLog";
import { Masks } from "@hooks/functions/Masks/Masks";
import { ZuToastifyStore } from "@hooks/stores/zustand-toastify-store";
import { formatName } from "@hooks/functions/formatName";
import { IPlatformDTO } from "@hooks/dtos/IPlatformDTO";
import {
  getPlatformLastHealthStatusInfo,
  getPlatformStatusInfo,
  IPlatformHealthStatusInfoDTO,
  IPlatformStatusInfoDTO,
} from "@hooks/functions/Platform";

// Icons
import { CgGlobe } from "react-icons/cg";
import { FaClock, FaServer, FaUser } from "react-icons/fa";
import { FiActivity, FiFileText, FiZap } from "react-icons/fi";
import {
  FaBroom,
  FaList,
  FaRegClock,
  FaRegEnvelope,
  FaWifi,
} from "react-icons/fa6";
import { useParams } from "react-router-dom";
import {
  IPlatformLogListDTO,
  PlatformLogStatusTypes,
  PlatformLogTypeTypes,
  PlatformLogWarningSentEnum,
  PlatformLogWarningSentTypes,
} from "@hooks/dtos/IPlatformLogDTO";
import {
  getPlatformLogStatus,
  platformLogStatusSelect,
  platformLogTypeSelect,
  platformLogWarningSentSelect,
} from "@hooks/functions/PlatformLog/status";
import { LoadingSkeleton } from "@components/skeleton-loading";
import { Pagination } from "@components/pagination";

interface IParams {
  page?: number;
  startDate?: string;
  endDate?: string;
}

const masks = new Masks();
export function PlatformHealth() {
  const { id: platformId } = useParams<string>();
  const {
    actions: { pushToastify },
  } = ZuToastifyStore();

  // --- Pagination values --- //
  const [page, setPage] = useState(1);
  const [take, setTake] = useState(10);
  const [totalPages, setTotalPages] = useState(1);

  // --- Filter values --- //
  const [type, setType] = useState<PlatformLogTypeTypes | undefined>();
  const [status, setStatus] = useState<PlatformLogStatusTypes | undefined>();
  const [warningSent, setWarningSent] = useState<
    PlatformLogWarningSentTypes | undefined
  >();
  const [resultTime, setResultTime] = useState("Todo Período");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [platform, setPlatform] = useState<IPlatformDTO>();
  const [platformLogs, setPlatformLogs] = useState<IPlatformLogListDTO[]>([]);
  const [platformLastLog, setPlatformLastLog] = useState<IPlatformLogListDTO>();
  const [platformHealthInfo, setPlatformHealthInfo] =
    useState<IPlatformHealthStatusInfoDTO>();
  const [platformStatusInfo, setPlatformStatusInfo] =
    useState<IPlatformStatusInfoDTO>();

  async function cleanFilters() {
    setStartDate("");
    setEndDate("");
    setType(undefined);
    setWarningSent(undefined);
    setStatus(undefined);
    setPage(1);
    setTotalPages(1);
    handleSubmit("", "");
  }

  function handleSubmit(_startDate: string, _endDate: string) {
    setIsLoading(true);
    setResultTime(
      _startDate.length + _endDate.length > 0
        ? `Período Selecionado`
        : `Todo Período`
    );

    const data = {
      page: 1,
      startDate: _startDate,
      endDate: _endDate,
    };

    setPage(1);
    getPlatformHealthCheck(data);
  }

  const formatResponseTime = (time?: number) => {
    if (!time) return "-";
    return `${masks.formatMilhar(time)} ms`;
  };

  const formatDate = (date?: Date | string) => {
    if (!date) return "-";
    return new Date(date).toLocaleString("pt-BR");
  };

  const getPlatformHealthCheck = async ({
    page,
    startDate,
    endDate,
  }: IParams) => {
    try {
      const params = `?id=${platformId}${type ? `&type=${type}` : ``}${
        status ? `&status=${status}` : ``
      }${
        warningSent ? `&warningSent=${warningSent}` : ``
      }&page=${page}&take=${take}&startDate=${startDate}&endDate=${endDate}`;

      const result = await api.get(`/misc/healthMonitor${params}`);
      const _platform = result?.data?.platform;
      const _platformLogs = result?.data?.platformLogs;
      const _platformLastLog = result?.data?.platformLastLog;

      const _platformLogList = _platformLogs?.logs;
      const _platformLogTotalPages = _platformLogs?.totalPages || 1;

      setPlatform(_platform);
      setPlatformLogs(_platformLogList);
      setPlatformLastLog(_platformLastLog);

      setTotalPages(_platformLogTotalPages);
      setIsLoading(false);
    } catch (error: any) {
      console.error(error);
      pushToastify({
        message:
          error?.message ||
          "Houve um erro ao carregar o Platform Health Check!",
        type: "error",
        theme: "dark",
        pauseOnHover: true,
      });
    }
  };

  const handlePlatformHealthCheck = async () => {
    try {
      Swal.fire({
        title: "Confirmar execução",
        text: "Tem certeza que deseja executar uma nova análise?",
        icon: "question",
        showCancelButton: true,
        confirmButtonColor: "#3085d6",
        cancelButtonColor: "#d33",
        confirmButtonText: "Sim, continuar.",
        cancelButtonText: "Cancelar",
        reverseButtons: true,
      }).then(async (result) => {
        if (result.isConfirmed) {
          // --- Set Loading and Send Info Message --- //
          setIsLoading(true);
          pushToastify({
            message: "Executando monitoramento, por favor aguarde...",
            type: "info",
            theme: "dark",
            pauseOnHover: true,
          });

          const data = {
            platforms: [platformId],
          };
          const result = await api.post(`/misc/healthMonitor/check`, data);

          // --- Load again the Platform Health Check List --- //
          const checkData = {
            page: 1,
            startDate: "",
            endDate: "",
          };
          getPlatformHealthCheck(checkData);
          cleanFilters();

          // --- Update Message --- //
          pushToastify({
            message: "Monitoramento executado com sucesso.",
            type: "success",
            theme: "dark",
            pauseOnHover: true,
          });
        }
      });
    } catch (error: any) {
      console.error(error);
      pushToastify({
        message:
          error?.message || "Houve um erro ao executar o Health Checker!",
        type: "error",
        theme: "dark",
        pauseOnHover: true,
      });
    }
  };

  useEffect(() => {
    const data = {
      page: page,
      startDate: startDate,
      endDate: endDate,
    };
    getPlatformHealthCheck(data);
  }, [page]);

  useEffect(() => {
    if (platform) {
      const healthCheckInfo = getPlatformLastHealthStatusInfo(
        platform.id,
        platform.lastHealthCheck
      );
      setPlatformHealthInfo(healthCheckInfo);

      const platformStatus = getPlatformStatusInfo(platform.status);
      setPlatformStatusInfo(platformStatus);
    }
  }, [platform]);

  return (
    <>
      {/* Main Page Header */}
      <Card className="position-relative">
        <div className="card-body">
          <div className="row align-items-center">
            <div className="col-md-8">
              <div className="d-flex align-items-center mb-3">
                <div className="bg-success bg-opacity-10 rounded p-3 me-3">
                  <FiActivity className="text-white" size={32} />
                </div>
                <div>
                  <h4 className="mb-1">Monitor de Saúde: {platform?.name}</h4>
                </div>
              </div>
              <p className="text-muted mb-0">
                Acompanhe o status e desempenho de todas as plataformas.
              </p>
              <p className="text-muted mb-0">
                Monitoramento contínuo de disponibilidade, tempo de resposta e
                qualidade de serviço.
              </p>
            </div>

            <div className="col-md-4 text-end">
              <div className="d-grid gap-2">
                <button
                  className="btn btn-primary btn-lg"
                  onClick={() => handlePlatformHealthCheck()}
                  disabled={isLoading}
                >
                  <FiActivity className="me-2" size={18} />
                  Checar Agora
                </button>
                <button
                  className="btn btn-outline-secondary"
                  onClick={() => {
                    pushToastify({
                      message: "Em construção...",
                      type: "info",
                      theme: "dark",
                      pauseOnHover: true,
                    });
                  }}
                >
                  <FaServer className="me-2" size={16} />
                  Configurações
                </button>
              </div>
            </div>
          </div>
        </div>
      </Card>

      {/* Page Content */}
      <div className="row">
        {isLoading ? (
          <>
            <div className="col-12 mb-3">
              <Skeleton height={180} />
            </div>
          </>
        ) : (
          platform && (
            <div className="col-12 mb-3">
              <Card
                className="border-start border-4"
                style={{
                  borderColor: `var(--bs-${platformHealthInfo?.badge || ""})`,
                }}
              >
                <div className="card-header">
                  <div className="d-flex align-items-center justify-content-between">
                    <div
                      className="d-flex align-items-center"
                      style={{ cursor: "pointer" }}
                      onClick={() => {
                        window.open(
                          platform.url,
                          "_blank",
                          "noopener,noreferrer"
                        );
                      }}
                    >
                      <img
                        className="me-3"
                        src={platformHealthInfo?.logo || ""}
                        alt="Logo"
                        style={{ height: "3rem" }}
                      />
                      <div>
                        <h5 className="mb-0">{platform.name}</h5>
                        {platform.description && (
                          <small className="text-muted">
                            {platform.description}
                          </small>
                        )}
                      </div>
                    </div>

                    <div className="d-flex align-items-center">
                      {(() => {
                        const StatusIcon = platformHealthInfo?.icon;
                        if (!StatusIcon) return null;
                        return (
                          <StatusIcon
                            size={24}
                            className={`text-${
                              platformHealthInfo?.badge || ""
                            } me-2`}
                          />
                        );
                      })()}
                      <span
                        className={`badge bg-${
                          platformHealthInfo?.badge || ""
                        } fs-6 px-3 py-2`}
                      >
                        {platformHealthInfo?.text || "Carregando..."}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="card-body">
                  {platformLastLog && (
                    <>
                      <div className="row g-3 mb-3">
                        <div className="col-md-3">
                          <div className="p-3 bg-light rounded">
                            <div className="text-muted small mb-1">
                              <FaServer size={14} className="me-1" /> Status
                              HTTP
                            </div>
                            <span
                              className={`badge ${
                                platformLastLog.httpStatus === 200
                                  ? "bg-success"
                                  : "bg-danger"
                              } fs-6 px-3 py-1`}
                            >
                              {platformLastLog.httpStatus}
                            </span>
                          </div>
                        </div>

                        <div className="col-md-3">
                          <div className="p-3 bg-light rounded">
                            <div className="text-muted small mb-1">
                              <FiZap size={14} className="me-1" /> Tempo de
                              Resposta
                            </div>
                            <div className="fw-semibold">
                              {formatResponseTime(platformLastLog.responseTime)}
                            </div>
                          </div>
                        </div>

                        {getPlatformLogPingInfo(platformLastLog?.ping) && (
                          <div className="col-md-3">
                            <div className="p-3 bg-light rounded">
                              <div className="text-muted small mb-1">
                                <FaWifi size={14} className="me-1" /> Ping
                              </div>
                              <span
                                className={`badge bg-${
                                  getPlatformLogPingInfo(platformLastLog?.ping)
                                    .class
                                } fs-6 px-3 py-1`}
                              >
                                {
                                  getPlatformLogPingInfo(platformLastLog?.ping)
                                    .text
                                }
                              </span>
                            </div>
                          </div>
                        )}

                        {getPlatformLogContentLoadedInfo(
                          platformLastLog?.contentLoaded
                        ) && (
                          <div className="col-md-3">
                            <div className="p-3 bg-light rounded">
                              <div className="text-muted small mb-1">
                                <FiFileText size={14} className="me-1" />
                                Conteúdo Carregado
                              </div>
                              <span
                                className={`badge bg-${
                                  getPlatformLogContentLoadedInfo(
                                    platformLastLog?.contentLoaded
                                  ).class
                                } fs-6 px-3 py-1`}
                              >
                                {
                                  getPlatformLogContentLoadedInfo(
                                    platformLastLog?.contentLoaded
                                  ).text
                                }
                              </span>
                            </div>
                          </div>
                        )}
                      </div>

                      <div className="row g-3">
                        {platformLastLog.type && (
                          <div className="col-md-4">
                            <div className="p-3 bg-light rounded">
                              <div className="text-muted small mb-1">
                                <FaList size={14} className="me-1" /> Tipo de
                                Log
                              </div>
                              <span
                                className={`badge bg-${
                                  getPlatformLogTypeInfo(platformLastLog?.type)
                                    .class
                                } fs-6 px-3 py-1`}
                              >
                                {
                                  getPlatformLogTypeInfo(platformLastLog?.type)
                                    .text
                                }
                              </span>
                            </div>
                          </div>
                        )}

                        {platformLastLog.customer && (
                          <div className="col-md-4">
                            <div className="p-3 bg-light rounded">
                              <div className="text-muted small mb-1">
                                <FaUser size={14} className="me-1" /> Usuário
                              </div>
                              <div className="fw-semibold">
                                {platformLastLog.customerName
                                  ? formatName(platformLastLog.customerName)
                                  : `ID: ${platformLastLog.customer}`}
                              </div>
                            </div>
                          </div>
                        )}

                        <div className="col-md-4">
                          <div className="p-3 bg-light rounded">
                            <div className="text-muted small mb-1">
                              <FaClock size={14} className="me-1" /> Última
                              Verificação
                            </div>
                            <div className="fw-semibold">
                              {formatDate(platform.updatedAt)}
                            </div>
                          </div>
                        </div>
                      </div>
                    </>
                  )}

                  {platformLastLog?.message && (
                    <div className="alert alert-warning mt-3 mb-0">
                      <strong>Mensagem:</strong> {platformLastLog.message}
                    </div>
                  )}
                </div>

                <div className="card-footer">
                  <div className="d-flex justify-content-between">
                    <div className="py-2">
                      <span
                        className="btn btn-outline-secondary me-3"
                        title={platformStatusInfo?.text}
                        style={{ cursor: "default" }}
                      >
                        Monitoramento:
                        {(() => {
                          const StatusIcon = platformStatusInfo?.icon;
                          if (!StatusIcon) return null;
                          return (
                            <StatusIcon
                              size={16}
                              className={`ms-1 text-${platformStatusInfo?.color}`}
                            />
                          );
                        })()}
                      </span>
                    </div>
                    <div className="py-2">
                      <a
                        href={platform.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn btn-sm btn-outline-secondary me-3"
                      >
                        <CgGlobe size={16} className="me-1" />
                        Acessar Página
                      </a>
                    </div>
                  </div>
                </div>
              </Card>
            </div>
          )
        )}
      </div>

      {/* Filters Area */}
      <div className="row">
        <div className="col-md-12">
          <Card>
            <div className="card-header">
              <div className="card-title">Filtrar Registros</div>
              <div className="card-category">
                Utilize o filtro para trazer um período específico.
              </div>
            </div>
            <div className="card-body">
              {/* Filters */}
              <div className="row">
                {/* Start Date */}
                <div className="col-md-3">
                  <div className="form-group">
                    <label htmlFor="startDate">Data Inicial</label>
                    <input
                      type="date"
                      className="form-control"
                      onChange={(e) => setStartDate(e.target.value)}
                      id="startDate"
                      value={startDate}
                    />
                  </div>
                </div>
                {/* End Date */}
                <div className="col-md-3">
                  <div className="form-group">
                    <label htmlFor="endDate">Data Final</label>
                    <input
                      type="date"
                      className="form-control"
                      onChange={(e) => setEndDate(e.target.value)}
                      id="endDate"
                      value={endDate}
                    />
                  </div>
                </div>
                {/* Status */}
                <div className="col-md-2">
                  <div className="form-group">
                    <label htmlFor="status">Status</label>
                    <select
                      className="form-control"
                      defaultValue={status || ""}
                      onChange={(e) => {
                        const value = e.target.value;
                        setStatus(
                          value === "0"
                            ? undefined
                            : (value as PlatformLogStatusTypes)
                        );
                      }}
                    >
                      <option value="">Selecione...</option>
                      {platformLogStatusSelect.map((x) => {
                        return (
                          <option key={x.status} value={x.status}>
                            {x.text}
                          </option>
                        );
                      })}
                    </select>
                  </div>
                </div>
                {/* Type */}
                <div className="col-md-2">
                  <div className="form-group">
                    <label htmlFor="type">Tipo</label>
                    <select
                      className="form-control"
                      defaultValue={type || ""}
                      onChange={(e) => {
                        const value = e.target.value;
                        setType(
                          value === "0"
                            ? undefined
                            : (value as PlatformLogTypeTypes)
                        );
                      }}
                    >
                      <option value="">Selecione...</option>
                      {platformLogTypeSelect.map((x) => {
                        return (
                          <option key={x.type} value={x.type}>
                            {x.text}
                          </option>
                        );
                      })}
                    </select>
                  </div>
                </div>
                {/* Warning Sent */}
                <div className="col-md-2">
                  <div className="form-group">
                    <label htmlFor="warningSent">Alerta</label>
                    <select
                      className="form-control"
                      defaultValue={warningSent || ""}
                      onChange={(e) => {
                        const value = e.target.value;
                        setWarningSent(
                          value === "0"
                            ? undefined
                            : (value as PlatformLogWarningSentTypes)
                        );
                      }}
                    >
                      <option value="">Selecione...</option>
                      {platformLogWarningSentSelect.map((x) => {
                        return (
                          <option key={x.type} value={x.type}>
                            {x.text}
                          </option>
                        );
                      })}
                    </select>
                  </div>
                </div>
              </div>
              {/* Filter Buttons */}
              <div className="row">
                <div className="d-flex justify-content-end">
                  <div className="form-group">
                    <button
                      className="btn btn-sm btn-success form-control px-5"
                      onClick={() => handleSubmit(startDate, endDate)}
                    >
                      <span className="text-white">Filtrar</span>
                    </button>
                  </div>
                  <div className="form-group">
                    <button
                      className="btn btn-sm btn-warning form-control"
                      onClick={cleanFilters}
                      title="Limpar todos os filtros."
                    >
                      <FaBroom className="text-white" />
                    </button>
                  </div>
                </div>
              </div>
            </div>
            <span className="position-absolute end-0 bg-info text-white p-2 rounded-end rounded-bottom-0">
              {resultTime}
            </span>
          </Card>
        </div>
      </div>

      {/* History Logs Table */}
      <div className="row">
        <div className="col-md-12">
          <Card>
            <div className="card-header">
              <div className="row">
                <div className="col-md-10">
                  <h4 className="card-title">Logs da Plataforma</h4>
                  <p className="card-category">Lista de todos os Logs</p>
                </div>
              </div>
            </div>
            <div className="card-body">
              <div className="table-responsive">
                <div
                  id="basic-datatables_wrapper"
                  className="dataTables_wrapper container-fluid dt-bootstrap4"
                >
                  <div
                    id="basic-datatables_length"
                    className="dataTables_length"
                  >
                    <table className="table table-striped">
                      <thead>
                        <tr role="row">
                          <th scope="col" className="fs-6">
                            #
                          </th>
                          <th>Usuário</th>
                          <th>
                            <FaRegClock /> Resp.
                          </th>
                          <th>Ping</th>
                          <th>Conteúdo</th>
                          <th>Status</th>
                          <th>Tipo</th>
                          <th>Info</th>
                          <th>Data</th>
                        </tr>
                      </thead>
                      <tbody>
                        {isLoading ? (
                          <tr>
                            <td colSpan={10} className="text-center">
                              <LoadingSkeleton />
                            </td>
                          </tr>
                        ) : (
                          <>
                            {platformLogs &&
                              platformLogs.length > 0 &&
                              platformLogs.map((platformLog) => {
                                const status = getPlatformLogStatus(
                                  platformLog.status
                                );
                                const logType = getPlatformLogTypeInfo(
                                  platformLog.type
                                );
                                const pingBadge = getPlatformLogPingInfo(
                                  platformLog.ping
                                );
                                const contentLoadedBadge =
                                  getPlatformLogContentLoadedInfo(
                                    platformLog.contentLoaded
                                  );
                                return (
                                  <tr key={platformLog?.id}>
                                    <td>{platformLog?.id || "~"}</td>
                                    <td>
                                      {formatName(
                                        platformLog?.customerName || "~"
                                      )}
                                    </td>
                                    <td>
                                      {`${masks.formatMilhar(
                                        platformLog?.responseTime
                                      )}ms`}
                                    </td>
                                    <td>
                                      <span
                                        className={`fs-6 badge badge-${pingBadge.class}`}
                                      >
                                        {pingBadge.text}
                                      </span>
                                    </td>
                                    <td>
                                      <span
                                        className={`fs-6 badge badge-${contentLoadedBadge.class}`}
                                      >
                                        {contentLoadedBadge.text}
                                      </span>
                                    </td>
                                    <td>
                                      <span
                                        className={`fs-6 badge badge-${status.color}`}
                                      >
                                        {status.text}
                                      </span>
                                    </td>
                                    <td>
                                      <span
                                        className={`fs-6 badge badge-${logType.class}`}
                                      >
                                        {logType.text}
                                      </span>
                                    </td>
                                    <td>
                                      {platformLog.warningSent ===
                                        PlatformLogWarningSentEnum.TRUE && (
                                        <FaRegEnvelope
                                          className={`text-${platformLog.message ? `primary` : `muted`}`}
                                          title={
                                            platformLog.message ||
                                            "Mensagem não identificada"
                                          }
                                          size={20}
                                        />
                                      )}
                                    </td>
                                    <td>
                                      {formatDate(platformLog?.createdAt)}
                                    </td>
                                  </tr>
                                );
                              })}

                            {isLoading ||
                              !platformLogs ||
                              (platformLogs?.length === 0 && (
                                <tr>
                                  <td colSpan={10} className="text-center">
                                    Nenhum registro encontrado.
                                  </td>
                                </tr>
                              ))}
                          </>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
              <Pagination
                page={page}
                setPage={setPage}
                totalPages={totalPages}
                maxPageShow={3}
              />
            </div>
          </Card>
        </div>
      </div>
    </>
  );
}
